import React, { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import {
  DatabaseBackup, Download, Trash2, Mail, RefreshCcw, CalendarClock,
} from "lucide-react";

const fmtSize = (bytes) => {
  if (bytes == null) return "—";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(2)} MB` : `${Math.round(bytes / 1024)} KB`;
};
const fmtDate = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") + " UTC" : "—");

export default function Backups() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backups");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runBackup = async (emailLink) => {
    setRunning(true);
    try {
      const { data } = await api.post(`/backups/run?email_link=${emailLink ? "true" : "false"}`);
      if (emailLink) {
        const to = (data.notified || []).join(", ");
        toast.success(to
          ? `Backup created — download link emailed to ${to}`
          : "Backup created (no admin emails configured to notify)");
      } else {
        toast.success("Backup created");
      }
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this backup permanently?")) return;
    try {
      await api.delete(`/backups/${id}`);
      toast.success("Backup deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="backups-page">
      <PageHeader
        eyebrow="Administration"
        title="Backups"
        subtitle="Full snapshots of the database + source code. A backup runs automatically every Sunday at 03:00 (Africa/Kampala) and the download link is emailed to administrators."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="backups-refresh">
              <RefreshCcw size={14} className="mr-1.5" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => runBackup(true)} disabled={running}
              data-testid="backups-run-email">
              <Mail size={14} className="mr-1.5" /> Run + email link
            </Button>
            <Button size="sm" onClick={() => runBackup(false)} disabled={running}
              className="bg-emerald-900 hover:bg-emerald-800" data-testid="backups-run-now">
              <DatabaseBackup size={14} className="mr-1.5" />
              {running ? "Backing up…" : "Run backup now"}
            </Button>
          </div>
        }
      />

      <div className="flex items-center gap-2 text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-5"
        data-testid="backups-schedule-note">
        <CalendarClock size={14} className="text-amber-600 shrink-0" />
        Weekly schedule: Sundays 03:00 EAT · the 8 most recent backups are retained · archives include .env secrets — store downloads safely.
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-16 text-center">Loading backups…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No backups yet"
          hint="Click 'Run backup now' to create your first snapshot, or wait for the Sunday schedule." />
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto" data-testid="backups-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3 text-right">Size</th>
                <th className="px-4 py-3 text-center">Collections</th>
                <th className="px-4 py-3 text-center">Documents</th>
                <th className="px-4 py-3">Trigger</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60"
                  data-testid={`backup-row-${b.id}`}>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-gray-800">{b.filename}</span>
                    {!b.exists && <Badge tone="red">file missing</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">{fmtSize(b.size_bytes)}</td>
                  <td className="px-4 py-3 text-center text-gray-700">{b.collections}</td>
                  <td className="px-4 py-3 text-center text-gray-700">{(b.documents ?? 0).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <Badge tone={b.trigger === "scheduled" ? "emerald" : "slate"}>{b.trigger}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      {b.exists && (
                        <a href={`${API_BASE}/backups/${b.id}/download`}
                          data-testid={`backup-download-${b.id}`}
                          className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md border border-gray-300 text-xs text-gray-700 hover:border-emerald-700 hover:text-emerald-800 transition-colors">
                          <Download size={13} /> Download
                        </a>
                      )}
                      <button onClick={() => remove(b.id)} data-testid={`backup-delete-${b.id}`}
                        className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md border border-gray-300 text-xs text-gray-500 hover:border-red-400 hover:text-red-600 transition-colors">
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
